import { Link } from "react-router-dom";
import { Zap, Clock, CalendarClock, Search, ArrowRight } from "lucide-react";
import { useCrashCourseSearch } from "@/hooks/use-offering-search";
import { Skeleton } from "@/components/ui/skeleton";
import { currencySymbol } from "@/lib/currency";
import { cn } from "@/lib/utils";
import { OFFERING_TYPE_THEME } from "@/lib/offeringTypeTheme";

function daysUntil(iso: string): number {
  const target = new Date(iso + "T00:00:00").getTime();
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((target - now.getTime()) / 86400000);
}

/** Teacher initials for the avatar; null-safe (teacherName is `string | null`). */
function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const initials = name.split(" ").map((n) => n[0]).filter(Boolean).join("").toUpperCase().slice(0, 2);
  return initials || "?";
}

/**
 * Deterministic accent per teacher so two same-titled courses under different
 * teachers are instantly distinguishable at a glance in the narrow rail.
 */
const AVATAR_ACCENTS = [
  "bg-indigo-100 text-indigo-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
  "bg-violet-100 text-violet-700",
];
function accentFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_ACCENTS[h % AVATAR_ACCENTS.length];
}

/** Round per-session price to at most 2 decimals, trimming trailing zeros. */
function formatPrice(n: number): string {
  return (Math.round(n * 100) / 100).toString();
}

export function CrashCoursesPanel({ subject, gradeBand, curriculum, onBrowse }: { subject?: string | null; gradeBand?: string | null; curriculum?: string | null; onBrowse?: (q: string) => void }) {
  // Per-type crash-course search (ADR-088/096): in-progress or starting-this-month
  // courses for the goal subject, ranked soonest-start-first so the rail and the
  // "See more" destination (/search/crash-course?sortBy=START_DATE) share the same ordering.
  //
  // Narrow by the goal's gradeBand + curriculum (the two high-signal, low-false-exclusion
  // dimensions — budget/mode are deliberately left out). Courses starting within 30 days
  // are scarce, though, so if narrowing empties the rail we fall back to subject-only
  // rather than show nothing.
  const narrowed = !!(gradeBand || curriculum);
  const base = { q: subject ?? undefined, sortBy: "START_DATE", startsWithinDays: 30, size: 3 };
  const primary = useCrashCourseSearch(
    { ...base, gradeBand: gradeBand ? [gradeBand] : undefined, curriculum: curriculum ? [curriculum] : undefined },
    !!subject,
  );
  const primaryEmpty = !primary.isLoading && !primary.isError && (primary.data?.content?.length ?? 0) === 0;
  const fallback = useCrashCourseSearch(base, !!subject && narrowed && primaryEmpty);
  const usingFallback = narrowed && primaryEmpty && (fallback.data?.content?.length ?? 0) > 0;

  const active = usingFallback ? fallback : primary;
  const data = active.data;
  const isLoading = primary.isLoading || (narrowed && primaryEmpty && fallback.isLoading);
  const isError = active.isError;
  const courses = data?.content ?? [];
  const total = data?.totalElements ?? 0;
  const hasMore = total > courses.length;

  // Keep "See more" in sync with what the rail actually shows: carry the goal
  // filters unless we fell back to subject-only.
  const seeMoreHref = (() => {
    if (!subject) return "/search/crash-course";
    const sp = new URLSearchParams({ q: subject, sortBy: "START_DATE" });
    if (!usingFallback && gradeBand) sp.set("gradeBand", gradeBand);
    if (!usingFallback && curriculum) sp.set("curriculum", curriculum);
    return `/search/crash-course?${sp.toString()}`;
  })();

  return (
    <div className="rounded-xl bg-slate-100/80 overflow-hidden">
      <div className={cn("px-4 py-3 border-b", OFFERING_TYPE_THEME.CRASH_COURSE.soft, OFFERING_TYPE_THEME.CRASH_COURSE.border)}>
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <span className={cn("inline-grid place-items-center w-6 h-6 rounded-md shrink-0", OFFERING_TYPE_THEME.CRASH_COURSE.iconChip)}><Zap className="w-3.5 h-3.5" /></span>
          Upcoming Crash Course You May want to explore
        </div>
        <p className="text-xs text-slate-400 mt-0.5">
          {subject ? `${subject} · in progress or starting this month` : "Set a subject goal to see crash courses"}
        </p>
      </div>

      {subject && isLoading ? (
        <div className="p-2 space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg bg-white ring-1 ring-slate-200/70 px-3 py-2.5 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      ) : !subject || isError || courses.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <Zap className="w-7 h-7 text-slate-200 mx-auto mb-2" />
          <p className="text-xs font-medium text-slate-400">
            {!subject ? "Add a subject to your goal" : "No crash courses available right now"}
          </p>
          {!subject && (
            <Link to="/my-goal" className="mt-2 inline-block text-xs text-amber-700 hover:text-amber-800 font-semibold">
              Set your goal →
            </Link>
          )}
          {subject && (
            onBrowse ? (
              <button type="button" onClick={() => onBrowse(subject)} className="mt-2 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-semibold cursor-pointer">
                <Search className="w-3 h-3" /> Browse crash courses
              </button>
            ) : (
              <Link to={`/search/crash-course?q=${encodeURIComponent(subject)}`} className="mt-2 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-semibold">
                <Search className="w-3 h-3" /> Browse crash courses
              </Link>
            )
          )}
        </div>
      ) : (
        <>
        <div className="p-2 space-y-2">
          {courses.map((c) => {
            const start = c.crashCourse?.courseStartDate ?? null;
            const inProgress = start != null && daysUntil(start) <= 0;
            const accent = accentFor(c.teacherName ?? c.offeringId);
            return (
              <Link
                key={c.offeringId}
                to={c.teacherHandle && c.slug ? `/t/${c.teacherHandle}/${c.slug}` : `/public/offering/${c.offeringId}`}
                className="block rounded-lg bg-white ring-1 ring-slate-200/70 px-3 py-2.5 hover:ring-amber-300 hover:shadow-sm transition-all duration-150"
              >
                <div className="flex items-start gap-2.5">
                  <div className={cn("shrink-0 w-7 h-7 rounded-full grid place-items-center text-xs font-bold", accent)}>
                    {getInitials(c.teacherName)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 leading-snug line-clamp-2">{c.title}</div>
                    <div className="text-xs font-medium text-slate-600 mt-0.5 truncate">{c.teacherName ?? "Teacher"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {start != null && (inProgress ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                      <Clock className="w-3 h-3" /> In progress
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                      <CalendarClock className="w-3 h-3" /> Starts in {daysUntil(start)}d
                    </span>
                  ))}
                  {c.minPrice != null && (
                    <span className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">{currencySymbol(c.currency ?? undefined)}{formatPrice(c.minPrice)}</span>
                      <span className="text-slate-400">/session</span>
                    </span>
                  )}
                </div>
                {start != null && (
                  <div className="text-xs text-slate-400 mt-1">
                    {inProgress ? "Started" : "Begins"} {new Date(start).toLocaleDateString()}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
        {hasMore && (
          <Link
            to={seeMoreHref}
            className="flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-semibold text-amber-700 hover:text-amber-800 hover:bg-slate-100 transition-colors border-t border-slate-200/70"
          >
            See more <ArrowRight className="w-3 h-3" />
          </Link>
        )}
        </>
      )}
    </div>
  );
}
